import { PROMPT_ROLES, type PromptRole } from '@ai-sales/shared'

/**
 * Привязки промптов к ролям — сборка и разборка для формы агента.
 *
 * Вынесено из экрана отдельным файлом ради одной ошибки, которую иначе
 * не проверить. До 25.09.2026 форма отправляла на сохранение список из
 * одной записи — системной, — потому что выбрать остальные роли было
 * негде. Сервер заменяет привязки целиком тем, что пришло: значит
 * привязка проверки ответа или готовности к звонку, появись она в базе
 * помимо формы, стёрлась бы первым же сохранением агента. Молча, без
 * следа и без ошибки.
 *
 * Такую ошибку не видно ни в типах, ни глазами на экране: форма
 * выглядит сохранившейся, а поведение агента меняется. Проверить её
 * можно только на самом списке, который уходит на сервер, — поэтому
 * список собирает отдельная функция, а не выражение внутри разметки.
 */
export type PromptBindings = Record<PromptRole, string | null>

/** Пустая раскладка: «не выбрано» — законное значение для всех ролей, кроме системной. */
export function emptyBindings(): PromptBindings {
  return Object.fromEntries(PROMPT_ROLES.map((role) => [role, null])) as PromptBindings
}

/** Что закреплено за версией агента — в вид, удобный форме. */
export function bindingsOf(
  version: { prompts: { role: PromptRole; promptVersionId: string }[] } | undefined | null,
): PromptBindings {
  const result = emptyBindings()

  for (const binding of version?.prompts ?? []) result[binding.role] = binding.promptVersionId

  return result
}

/**
 * Список привязок для отправки — или `null`, если отправлять нельзя.
 *
 * `null` возвращается, когда не выбрана системная роль. Пустой список
 * у сервера означает «убрать все привязки», а системная обязательна:
 * случайное сохранение на недогруженной форме сделало бы агента
 * непубликуемым. Отсутствие поля в запросе означает «не трогать» — это
 * и нужно.
 *
 * Роль без выбранного промпта в список не попадает и тем самым
 * отвязывается. Это единственный способ выключить проверку ответа
 * моделью или проверку готовности, и он должен работать так же
 * надёжно, как включение.
 */
export function bindingsPayload(
  bindings: PromptBindings,
): { role: PromptRole; promptVersionId: string }[] | null {
  if (!bindings.system) return null

  return PROMPT_ROLES.flatMap((role) => {
    const promptVersionId = bindings[role]

    return promptVersionId ? [{ role, promptVersionId }] : []
  })
}
